import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type CreateSubstance, type Substance } from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

export const useGetSubstancesQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['substances'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/substances')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useCreateSubstanceMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (substance: CreateSubstance) => {
      const { data, error } = await authFetch().POST('/substances', {
        body: substance
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['substances']
      })
    }
  })
}

export const useUpdateSubstanceMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      id,
      substance
    }: {
      id: number
      substance: CreateSubstance
    }) => {
      const { data, error } = await authFetch().PUT(
        '/substances/{substanceId}',
        {
          params: {
            path: { substanceId: id }
          },
          body: substance
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data as Substance
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['substances']
      })
    }
  })
}

export const useDeleteSubstanceMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (substanceId: number) =>
      await authFetch().DELETE('/substances/{substanceId}', {
        params: {
          path: { substanceId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['substances']
      })
    }
  })
}
